import type {ChangeEvent, ReactElement} from 'react';
import {useContext, useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {setComponentStyle, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {flashSpacingHighlight} from '@EmailMakerReact/lib/email-maker-spacing-highlight-library';
import type {ExtendedControlValues, SliderContainerProps} from '../../slider-control.types';
import {DEFAULT_EXTENDED_CONTROL_ICONS, SLIDER_CSS_PROPS, SLIDER_HIDE_INPUT_PROPS, SPACING_HIGHLIGHT_PROPS} from '../../slider-control.types';
import {buildRangeFillStyle, dispatchCustomProperty, getExtendedPrefixes, parseAndClamp, parseRawCssToNumeric, readExtendedOpen} from '../../slider-control.utils';
import {SliderBody} from '../slider-body/slider-body';
import {ExtendedControlInput} from '../extended-control-input/extended-control-input';
import styles from '../../slider-control.module.scss';

export function SliderContainer({descriptor, effectiveMax, component, responsiveTag, activeProperty, isHidden}: SliderContainerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);

  const rawValue = useStyleProperty(editor, component, activeProperty);
  const numericValue = parseRawCssToNumeric(rawValue ?? '', {...descriptor, property: activeProperty});

  const step = descriptor.step ?? 1;
  const min = descriptor.min;
  const max = effectiveMax;
  const unit = descriptor.unit;
  const hideRangeInput = SLIDER_HIDE_INPUT_PROPS.has(descriptor.property);

  const [isInputActive, setIsInputActive] = useState(false);
  const [extendedOpen, setExtendedOpen] = useState((): boolean => readExtendedOpen(component));
  const [selectedSide, setSelectedSide] = useState<keyof ExtendedControlValues | null>(null);

  useEffect((): void => {
    setExtendedOpen(readExtendedOpen(component));
    setSelectedSide(null);
  }, [component]);

  const extendedPrefixes = descriptor.extendedControls ? getExtendedPrefixes(descriptor.property) : {top: '', bottom: '', left: '', right: ''};
  const rawTop = useStyleProperty(editor, component, extendedPrefixes.top);
  const rawBottom = useStyleProperty(editor, component, extendedPrefixes.bottom);
  const rawLeft = useStyleProperty(editor, component, extendedPrefixes.left);
  const rawRight = useStyleProperty(editor, component, extendedPrefixes.right);

  const extendedValues: ExtendedControlValues | null = descriptor.extendedControls
    ? {
        top: rawTop ? parseInt(rawTop, 10) : numericValue,
        bottom: rawBottom ? parseInt(rawBottom, 10) : numericValue,
        left: rawLeft ? parseInt(rawLeft, 10) : numericValue,
        right: rawRight ? parseInt(rawRight, 10) : numericValue,
      }
    : null;

  const isMixed =
    extendedValues !== null && (extendedValues.top !== extendedValues.bottom || extendedValues.top !== extendedValues.left || extendedValues.top !== extendedValues.right);

  const displayValue: number | 'mixed' = extendedValues && isMixed ? 'mixed' : numericValue;

  function writeStyle(prop: string, val: number): void {
    if (!editor) {
      return;
    }
    const cssValue = `${val}${unit}`;
    setComponentStyle(editor, {property: prop, value: cssValue, component});
    dispatchCustomProperty(editor, prop, val);
    if (SPACING_HIGHLIGHT_PROPS.has(prop)) {
      flashSpacingHighlight(component);
    }
  }

  function writeExtendedSide(side: keyof ExtendedControlValues, val: number): void {
    if (!editor) {
      return;
    }
    const prefixes = getExtendedPrefixes(descriptor.property);
    const prop = prefixes[side];
    writeStyle(prop, val);
  }

  function writeAllSides(val: number): void {
    if (!editor) {
      return;
    }
    const prefixes = getExtendedPrefixes(descriptor.property);
    if (descriptor.property !== SLIDER_CSS_PROPS.MARGIN) {
      writeStyle(prefixes.left, val);
      writeStyle(prefixes.right, val);
    }
    writeStyle(prefixes.top, val);
    writeStyle(prefixes.bottom, val);
    writeStyle(descriptor.property, val);
  }

  function handleSliderChange(e: ChangeEvent<HTMLInputElement>): void {
    const val = parseAndClamp(e.target.value, min, max);
    if (descriptor.extendedControls) {
      writeAllSides(val);
    } else {
      writeStyle(activeProperty, val);
    }
  }

  function handleTextInputChange(val: number): void {
    const clamped = parseAndClamp(String(val), min, max);
    if (descriptor.extendedControls) {
      writeAllSides(clamped);
    } else {
      writeStyle(activeProperty, clamped);
    }
  }

  function handleStep(direction: 1 | -1): void {
    const next = parseAndClamp(String(numericValue + direction * step), min, max);
    if (descriptor.extendedControls) {
      writeAllSides(next);
    } else {
      writeStyle(activeProperty, next);
    }
  }

  function toggleExtended(): void {
    const next = !extendedOpen;
    setExtendedOpen(next);
    component.addAttributes({'data-has-extended-controls': next ? 1 : 0});
  }

  const extIcons = descriptor.extendedControlIcons ?? DEFAULT_EXTENDED_CONTROL_ICONS;

  const moreOptionsIconDefault = descriptor.property === SLIDER_CSS_PROPS.PADDING ? 'icon-edge-spacing' : 'icon-corner';

  const moreOptionsIconForSide: Record<string, string> = {
    top: extIcons.first,
    bottom: extIcons.second,
    left: extIcons.third,
    right: extIcons.fourth,
  };

  function renderMoreOptionsButton(): ReactElement | null {
    if (!descriptor.extendedControls) {
      return null;
    }
    const activeIcon = selectedSide ? moreOptionsIconForSide[selectedSide] : moreOptionsIconDefault;
    const iconColorClass = selectedSide ? styles.iconMixed : '';
    return (
      <button
        aria-label={window.i18next.t('pmwjs_more_options')}
        aria-pressed={extendedOpen}
        className={`${styles.moreOptionsButton} ${extendedOpen ? styles.moreOptionsButtonSelected : ''}`}
        onClick={toggleExtended}
        type="button"
      >
        <Icon aria-hidden className={`more-options-icon ${iconColorClass}`} icon={activeIcon} size={IconSize.SIZE_ICON_20} />
      </button>
    );
  }

  function renderExtendedControls(): ReactElement | null {
    if (!descriptor.extendedControls || !extendedValues) {
      return null;
    }
    const sides: {key: keyof ExtendedControlValues; ariaKey: string; iconClass: string; inputClass?: string}[] = [
      {key: 'top', ariaKey: 'pmwjs_top', iconClass: extIcons.first, inputClass: styles.extendedTop},
      {key: 'bottom', ariaKey: 'pmwjs_bottom', iconClass: extIcons.second},
      {key: 'left', ariaKey: 'pmwjs_left', iconClass: extIcons.third},
      {key: 'right', ariaKey: 'pmwjs_right', iconClass: extIcons.fourth},
    ];
    const showLeftRight = descriptor.property !== SLIDER_CSS_PROPS.MARGIN;
    return (
      <div className={`${styles.extendedContainer} ${extendedOpen ? '' : styles.extendedContainerHidden}`}>
        {sides
          .filter((s) => showLeftRight || (s.key !== 'left' && s.key !== 'right'))
          .map((s) => (
            <ExtendedControlInput
              ariaLabel={window.i18next.t(s.ariaKey)}
              iconClass={s.iconClass}
              inputClassName={s.inputClass}
              isSelected={selectedSide === s.key}
              key={s.key}
              max={max}
              min={min}
              onBlur={(): void => {
                setSelectedSide(null);
              }}
              onChange={(val): void => {
                writeExtendedSide(s.key, val);
              }}
              onFocus={(): void => {
                setSelectedSide(s.key);
              }}
              step={step}
              value={extendedValues[s.key]}
            />
          ))}
      </div>
    );
  }

  function renderLabel(): ReactElement {
    return (
      <span className={styles.label}>
        {responsiveTag ? <Text val={responsiveTag} size={TextSize.SM} bold className={styles.responsiveTag} /> : null}
        <Text val={window.i18next.t(descriptor.labelKey)} size={TextSize.MD} />
      </span>
    );
  }

  return (
    <div className={`${styles.sliderContainer} ${isHidden ? styles.sliderContainerHidden : ''}`}>
      <div className={styles.labelRow}>
        {renderLabel()}
        <SliderBody
          descriptor={descriptor}
          displayValue={displayValue}
          effectiveMax={effectiveMax}
          inputOptionsClassName={descriptor.extendedControls ? styles.inputOptionsExtended : undefined}
          isInputActive={isInputActive}
          numericValue={numericValue}
          onActivate={(): void => {
            setIsInputActive(true);
          }}
          onDeactivate={(): void => {
            setIsInputActive(false);
          }}
          onStep={handleStep}
          onTextChange={handleTextInputChange}
          renderExtraButton={renderMoreOptionsButton}
        />
      </div>
      <input
        aria-label={descriptor.property}
        className={`${styles.rangeInput} ${hideRangeInput ? styles.rangeInputHidden : ''}`}
        max={max}
        min={min}
        onChange={handleSliderChange}
        step={step}
        style={buildRangeFillStyle(isNaN(numericValue) ? min : numericValue, min, max)}
        type="range"
        value={isNaN(numericValue) ? min : numericValue}
      />
      {renderExtendedControls()}
    </div>
  );
}
